import React, { useCallback, useState } from "react";
import { Modal } from "react-bootstrap";
import Image from "react-bootstrap/esm/Image";
import { Grid } from "../../components/grid";
import { Location } from "../../data";
import { useMediaQuery } from "../../hooks";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { Button } from "../../offering/button";
import { Dialog } from "../../offering/dialog";
import { Spacer } from "../../offering/spacer";
import { Text } from "../../offering/text";
import "./membership_location_list.css";

const N_OF_LOCATIONS_TO_SHOW_DESKTOP = 4;
const N_OF_LOCATIONS_TO_SHOW_MOBILE = 4;

interface MembershipLocationsProps {
  locations: Location[];
}

export function MembershipLocations(props: MembershipLocationsProps) {
  const { locations } = props;
  const { fbt } = useI18n();
  const [showMore, setShowMore] = useState(false);
  const mq = useMediaQuery();

  const onShowMorePress = useCallback(() => {
    setShowMore(!showMore);
  }, [showMore]);
  const isMobile = mq.device === "mobile";
  const numOfLocationsToShow = isMobile
    ? N_OF_LOCATIONS_TO_SHOW_MOBILE
    : N_OF_LOCATIONS_TO_SHOW_DESKTOP;
  const showedLocations = locations?.slice(0, numOfLocationsToShow);
  const shouldViewShowMoreButton = locations.length > numOfLocationsToShow;

  return (
    <>
      <div>
        <h3>{fbt("Included Locations")}</h3>
        <Spacer size={isMobile ? 16 : 24} />

        {showedLocations.map((loc) => {
          return (
            <div key={loc.id}>
              <span>
                <strong>{loc.name}</strong> - {loc.streetAddress}, {loc.city} -{" "}
                {loc.country}
              </span>
              <Spacer size={8} />
            </div>
          );
        })}

        {shouldViewShowMoreButton && (
          <>
            <Spacer size={12} />
            <div style={{ width: isMobile ? "100%" : "320px" }}>
              <Button
                onPress={onShowMorePress}
                title={`Show all ${locations.length} locations`}
                appearance="filled"
                size="md"
              />
            </div>
          </>
        )}

        <Dialog
          onRequestClose={() => setShowMore(false)}
          isOpen={showMore}
          header={
            <h3 style={{ textAlign: "center", paddingTop: 24 }}>
              {fbt("Included Locations")}
            </h3>
          }
        >
          <div style={{ padding: 24 }}>
            <Spacer size={24} />
            {!!locations.length &&
              locations.map((loc) => {
                return (
                  <div key={loc.id}>
                    <Text size="md" weight="600">
                      {loc.name}{" "}
                      <Text size="sm">
                        - {loc.streetAddress}, {loc.city} - {loc.country}
                      </Text>
                    </Text>

                    <Spacer size={8} />
                  </div>
                );
              })}
          </div>
        </Dialog>
      </div>
    </>
  );
}

export function MembershipLocationsV2(props: MembershipLocationsProps) {
  const { locations } = props;
  const { fbt } = useI18n();
  const [showMore, setShowMore] = useState(false);
  const mq = useMediaQuery();

  const onShowMorePress = useCallback(() => {
    setShowMore(!showMore);
  }, [showMore]);
  const isMobile = mq.device === "mobile";
  const numOfLocationsToShow = isMobile
    ? N_OF_LOCATIONS_TO_SHOW_MOBILE
    : N_OF_LOCATIONS_TO_SHOW_DESKTOP;
  const showedLocations = locations?.slice(0, numOfLocationsToShow);
  const shouldViewShowMoreButton = locations.length > numOfLocationsToShow;

  return (
    <>
      <div data-testid="locations">
        <h3 className="text-uppercase">{fbt("Locations")}</h3>
        <Spacer size={isMobile ? 16 : 24} />
        <Grid itemsPerRow={isMobile ? 1 : 2} gap={24}>
          {showedLocations.map((loc) => {
            return (
              <div
                className="membership-location-card"
                key={loc.id}
                data-testid="location-item"
              >
                <Image
                  src={loc.images?.[0]?.small.url}
                  style={{ width: 80, height: 60 }}
                />
                <div>
                  <div className="text-primary">{loc.name}</div>
                  <div>{`${loc.city}, ${loc.country}`}</div>
                  <div>{loc.streetAddress}</div>
                </div>
              </div>
            );
          })}
        </Grid>

        {shouldViewShowMoreButton && (
          <>
            <Spacer size={12} />
            <div
              style={{ width: isMobile ? "100%" : "320px" }}
              data-testid="locations-view-more"
            >
              <Button
                onPress={onShowMorePress}
                title={`Show all ${locations.length} locations`}
                appearance="filled"
                size="md"
              />
            </div>
          </>
        )}

        <Modal
          scrollable
          show={showMore}
          data-testid="view-all-locations-modal"
        >
          <Modal.Header closeButton onClick={() => setShowMore(false)}>
            <h3 style={{ textAlign: "center", margin: 0 }}>
              {fbt("Locations")}
            </h3>
          </Modal.Header>

          <Modal.Body>
            {!!locations.length &&
              locations.map((loc) => {
                return (
                  <div key={loc.id}>
                    <div>
                      <strong>{loc.name}</strong>
                    </div>
                    <div>{`${loc.city}, ${loc.country}`}</div>
                    <div>{loc.streetAddress}</div>
                    <Spacer size={12} />
                  </div>
                );
              })}
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
}
